import { useEffect, useRef } from 'react'
import { hapticLight, hapticMedium } from '../utils/haptic'

interface UseEditorSwipeOptions {
  ref: React.RefObject<HTMLElement>
  enabled: boolean                    // 移动端特定场景启用
  sidebarOpen: boolean
  sidebarFullscreen: boolean
  onOpenSidebar: () => void           // 关闭 → 半屏
  onExpandSidebar: () => void         // 半屏 → 全屏
  onCloseSidebar: () => void          // 半屏 → 关闭
  onSwitchToCrdt?: () => void         // 速记全屏 → 编辑模式（可选：通用场景不传）
}

/**
 * 移动端编辑器区域水平滑动 Hook（场景 2：速记模式）
 *
 * 功能：
 * - 侧边栏关闭 + 向右滑 → 侧边栏半屏
 * - 侧边栏半屏 + 向右滑 → 侧边栏全屏（编辑器淡出）
 * - 侧边栏半屏 + 向左滑 → 侧边栏关闭
 * - 侧边栏关闭 + 向左滑 → 切换到编辑模式
 *
 * 设计遵循：
 * - Pointer Events API（W3C 标准）
 * - 阈值触发（80px），避免与编辑器内部交互冲突
 * - 方向锁定 1.5:1（垂直滑动不触发，保持编辑器垂直滚动）
 * - useRef 存储最新回调和状态，避免 useEffect 依赖重建
 * - capture 阶段监听 pointerdown
 */
export function useEditorSwipe(options: UseEditorSwipeOptions) {
  const {
    ref, enabled,
    sidebarOpen, sidebarFullscreen,
    onOpenSidebar, onExpandSidebar, onCloseSidebar, onSwitchToCrdt,
  } = options

  // useRef 存储最新值，避免成为 useEffect 依赖
  const stateRef = useRef({ sidebarOpen, sidebarFullscreen })
  const callbacksRef = useRef({ onOpenSidebar, onExpandSidebar, onCloseSidebar, onSwitchToCrdt })

  useEffect(() => {
    stateRef.current = { sidebarOpen, sidebarFullscreen }
    callbacksRef.current = { onOpenSidebar, onExpandSidebar, onCloseSidebar, onSwitchToCrdt }
  })

  useEffect(() => {
    const el = ref.current
    if (!el || !enabled) return

    const THRESHOLD = 80
    const DIRECTION_LOCK_RATIO = 1.5

    const state = {
      startX: 0, startY: 0, isTracking: false,
      isTriggered: false, directionLocked: false, pointerId: -1,
    }

    const cleanup = () => {
      state.isTracking = false
      state.isTriggered = false
      state.directionLocked = false
      state.pointerId = -1
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      document.removeEventListener('pointercancel', handlePointerCancel)
    }

    const handleSwipe = (deltaX: number) => {
      const { sidebarOpen, sidebarFullscreen } = stateRef.current
      const cb = callbacksRef.current

      if (deltaX > THRESHOLD) {
        // 右滑：关闭→半屏，半屏→全屏
        if (!sidebarOpen) { cb.onOpenSidebar(); hapticLight() }
        else if (!sidebarFullscreen) { cb.onExpandSidebar(); hapticLight() }
      } else if (deltaX < -THRESHOLD) {
        // 左滑：半屏→关闭，关闭→切换编辑模式（仅当提供了 onSwitchToCrdt 时）
        if (sidebarOpen && !sidebarFullscreen) { cb.onCloseSidebar(); hapticLight() }
        else if (!sidebarOpen && cb.onSwitchToCrdt) { cb.onSwitchToCrdt(); hapticMedium() }
      }
    }

    const handlePointerMove = (e: PointerEvent) => {
      if (!state.isTracking || state.isTriggered) return
      if (e.pointerId !== state.pointerId) return
      const deltaX = e.clientX - state.startX
      const deltaY = e.clientY - state.startY
      if (!state.directionLocked) {
        if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return
        if (Math.abs(deltaY) > Math.abs(deltaX) * DIRECTION_LOCK_RATIO) {
          cleanup(); return  // 垂直滑动，放弃
        }
        state.directionLocked = true
      }
      if (Math.abs(deltaX) >= THRESHOLD) {
        state.isTriggered = true
        handleSwipe(deltaX)
        cleanup()
      }
    }

    const handlePointerUp = (e: PointerEvent) => {
      if (!state.isTracking || state.isTriggered) { cleanup(); return }
      if (e.pointerId !== state.pointerId) { cleanup(); return }
      const deltaX = e.clientX - state.startX
      const deltaY = e.clientY - state.startY
      if (state.directionLocked || Math.abs(deltaY) < Math.abs(deltaX) * DIRECTION_LOCK_RATIO) {
        if (Math.abs(deltaX) >= THRESHOLD) {
          state.isTriggered = true
          handleSwipe(deltaX)
        }
      }
      cleanup()
    }

    const handlePointerCancel = () => cleanup()

    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return
      if (state.isTracking) return
      state.startX = e.clientX
      state.startY = e.clientY
      state.isTracking = true
      state.isTriggered = false
      state.directionLocked = false
      state.pointerId = e.pointerId
      document.addEventListener('pointermove', handlePointerMove)
      document.addEventListener('pointerup', handlePointerUp)
      document.addEventListener('pointercancel', handlePointerCancel)
    }

    el.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      el.removeEventListener('pointerdown', handlePointerDown, true)
      cleanup()
    }
  }, [ref, enabled])
}
